import {all, call, put, takeEvery} from 'redux-saga/effects'
import {
  UserRolesActionTypes,
  fetchError,
  fetchSuccess,
  createError,
  createSuccess,
  deleteError,
  deleteSuccess,
  updateSuccess,
  updateError,
  fetchItemSuccess,
  fetchItemError,
} from '@actions/user-role';
import userRoleApi from '@api/user-role';
import {ReduxAction} from "@actions/index";
import {RequestData} from '@models/axios';


function* handleFetch({payload}: ReduxAction) {
  try {
    const res = yield call(userRoleApi.getList, payload);
    yield put(fetchSuccess(res))
  } catch (err) {
    yield put(fetchError(err))
  }
}


function* handleDelete({payload}: ReduxAction) {
  try {
    yield call(userRoleApi.delete, payload);
    yield put(deleteSuccess(payload.params))
  } catch (err) {
    yield put(deleteError(err))
  }
}


/**
 * This function is called after a userRole is created or updated successfully and will update all menus and resources of it
 * @param userRole_id
 * @param payload
 */
function* updateRelatedEntities(userRole_id: string, payload) {
  try {
    const params = {id: userRole_id};
    const {resources, menus} = payload;
    
    if (resources) {
      yield updateUserRoleResources({
        params,
        payload: resources
      });
    }
    if (menus) {
      yield updateUserRoleMenus({
        params,
        payload: menus
      });
    }
  } catch (e) {
    console.log(e)
  }
}


/**
 * This function will handle add and delete resource form a userRole
 * @param params
 * @param payload
 */
function* updateUserRoleResources({params, payload}: RequestData) {
  try {
    const {new_resources, deleted_resources} = payload;
    if (new_resources.length > 0) {
      yield call(userRoleApi.addResources, {
        params,
        payload: {
          resource_ids: new_resources
        }
      });
    }
    if (deleted_resources.length > 0) {
      yield call(userRoleApi.deleteResources, {
        params,
        payload: {
          resource_ids: deleted_resources
        }
      });
    }
  } catch (e) {
    console.log(e)
  }
}

/**
 * This function will handle add and delete menus form a userRole
 * @param params
 * @param payload
 */
function* updateUserRoleMenus({params, payload}: RequestData) {
  try {
    const {new_menus, deleted_menus} = payload;
    if (new_menus.length > 0) {
      yield call(userRoleApi.addMenus, {
        params,
        payload: {
          menu_ids: new_menus
        }
      });
    }
    if (deleted_menus.length > 0) {
      yield call(userRoleApi.deleteMenus, {
        params,
        payload: {
          menu_ids: deleted_menus
        }
      });
    }
  } catch (e) {
    console.log(e)
  }
}

function* handleCreate({payload}: ReduxAction) {
  try {
    const {userRoleDetails} = payload.payload;
    const response = yield call(userRoleApi.create, {
      payload: userRoleDetails
    });
    
    const userRole = response.data;
    yield updateRelatedEntities(userRole.id, payload.payload);
    yield put(createSuccess(userRole));
    payload.onSuccessCallback && payload.onSuccessCallback(userRole);
  } catch (err) {
    yield put(createError(err));
    payload.onErrorCallback && payload.onErrorCallback(err);
  }
}


function* handleUpdate({payload}: ReduxAction) {
  try {
    const {userRoleDetails} = payload.payload;
    const response = yield call(userRoleApi.update, {
      ...payload,
      payload: userRoleDetails
    });
    
    const userRole = response.data;
    yield updateRelatedEntities(userRole.id, payload.payload);
    yield put(updateSuccess(userRole));
    payload.onSuccessCallback && payload.onSuccessCallback();
  } catch (err) {
    createErrorMessage(err.data.meta);
    yield put(updateError(err));
    payload.onErrorCallback && payload.onErrorCallback(err);
  }
}


function* handleFetchItem({payload}: ReduxAction) {
  try {
    const res = yield call(userRoleApi.getItem, payload);
    payload.onSuccessCallback && payload.onSuccessCallback();
    yield put(fetchItemSuccess(res.data))
  } catch (err) {
    yield put(fetchItemError(err.stack!))
  }
}


// We can also use `fork()` here to split our saga into multiple watchers.
function* userRolesSaga() {
  yield all([
    yield takeEvery(UserRolesActionTypes.FETCH_REQUEST, handleFetch),
    yield takeEvery(UserRolesActionTypes.DELETE_REQUEST, handleDelete),
    yield takeEvery(UserRolesActionTypes.CREATE_REQUEST, handleCreate),
    yield takeEvery(UserRolesActionTypes.UPDATE_REQUEST, handleUpdate),
    yield takeEvery(UserRolesActionTypes.FETCH_ITEM_REQUEST, handleFetchItem),
  ])
}

export default userRolesSaga;
